import { useEffect, useState } from "react";
import { api } from "../lib/api.js";
import { RichTextEditor } from "../components/RichTextEditor.js";

/**
 * Edits the single, live release-notes document (stored as one file in R2, served
 * to audioswytch.co.uk/release-notes). Not per-release: one document you format
 * and publish; it goes live the moment you save.
 */
export function ReleaseNotesEditor() {
  const [loaded, setLoaded] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get<{ html: string }>("/admin/release-notes")
      .then((r) => {
        setLoaded(r.html ?? "");
        setDraft(r.html ?? "");
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load release notes"));
  }, []);

  async function save() {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const r = await api.put<{ ok: boolean; html: string }>("/admin/release-notes", { html: draft });
      // Show the sanitised version that was actually stored.
      setLoaded(r.html);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  if (loaded === null && !error) return <p>Loading…</p>;

  return (
    <div>
      <div className="row-between">
        <h1>Release notes</h1>
        <div className="button-row">
          <a className="button" href="https://audioswytch.co.uk/release-notes" target="_blank" rel="noreferrer">
            View live ↗
          </a>
          <button className="primary" onClick={save} disabled={saving}>
            {saving ? "Publishing…" : "Save & publish"}
          </button>
        </div>
      </div>
      <p className="muted-subtitle" style={{ margin: "0 0 14px" }}>
        One live document, shown on audioswytch.co.uk/release-notes and linked from the app&rsquo;s About screen.
        Changes go live as soon as you publish. Newest release at the top: <b>+ New release</b> inserts a version block;
        a <b>Version</b> heading draws the separator line on the website automatically, the line under it is the date,
        <b>Section</b> is for Fixed / Changed. Keep bullets to one line.
      </p>
      {error && <p className="error-text">{error}</p>}
      {saved && <p className="ok-text">Saved and published. It’s live now.</p>}
      {loaded !== null && <RichTextEditor initialHtml={loaded} onChange={setDraft} />}
    </div>
  );
}
